import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import ValueBadge from '../components/ValueBadge';
import VibeTags from '../components/VibeTags';
import PointsBar from '../components/PointsBar';
import BudgetTracker from '../components/BudgetTracker';
import DestImage from '../components/DestImage';
import { useTripBuilder } from '../TripBuilderContext';

const tabs = [
  { id: 'flights', label: 'Flights' },
  { id: 'hotels', label: 'Hotels' },
  { id: 'activities', label: 'Things to Do' },
  { id: 'restaurants', label: 'Places to Eat' },
];

export default function DestinationDetail({ trip, compareIds, toggleCompare }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dest, setDest] = useState(null);
  const [activities, setActivities] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selections, setSelections] = useState([]);
  const [activeTab, setActiveTab] = useState('flights');
  const [refreshBudget, setRefreshBudget] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalItem, setModalItem] = useState(null);
  const [modalType, setModalType] = useState(null);

  const { amexTotal: amex, chaseTotal: chase } = useTripBuilder();

  useEffect(() => {
    Promise.all([
      api.getDestination(id),
      api.getActivities(id),
      api.getRestaurants(id),
      api.getSelections(),
    ]).then(([d, a, r, s]) => {
      setDest(d); setActivities(a); setRestaurants(r); setSelections(s);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const openModal = (item, type) => { setModalItem(item); setModalType(type); };
  const closeModal = () => { setModalItem(null); setModalType(null); };

  const isSelected = (type, refId) => selections.some(s => s.selection_type === type && s.reference_id === refId);

  const addToTrip = async (type, refId, pointsCost, cashCost, sourceProgram, notes) => {
    await api.addSelection({
      selection_type: type,
      reference_id: refId,
      destination_id: parseInt(id),
      points_cost: pointsCost,
      cash_cost: cashCost,
      source_program: sourceProgram,
      notes,
    });
    const updated = await api.getSelections();
    setSelections(updated);
    setRefreshBudget(prev => prev + 1);
  };

  const removeFromTrip = async (type, refId) => {
    const sel = selections.find(s => s.selection_type === type && s.reference_id === refId);
    if (sel) {
      await api.removeSelection(sel.id);
      const updated = await api.getSelections();
      setSelections(updated);
      setRefreshBudget(prev => prev + 1);
    }
  };

  const handleSelect = async () => {
    await api.selectDestination(id);
    navigate('/plan');
  };

  if (loading) return (
    <div className="flex gap-6">
      <div className="flex-1 min-w-0 space-y-6">
        <div className="animate-shimmer h-4 w-40 rounded" />
        <div className="rounded-2xl border border-brand-ink/10 overflow-hidden">
          <div className="animate-shimmer h-48" />
          <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[0,1,2,3].map(i => <div key={i} className="animate-shimmer h-16 rounded-lg" style={{ animationDelay: `${i * 100}ms` }} />)}
          </div>
        </div>
        <div className="flex gap-2">{[0,1,2,3].map(i => <div key={i} className="animate-shimmer h-10 w-24 rounded-lg" />)}</div>
        <div className="space-y-3">{[0,1,2].map(i => <div key={i} className="animate-shimmer h-32 rounded-xl" style={{ animationDelay: `${i * 100}ms` }} />)}</div>
      </div>
      <div className="hidden lg:block w-72 flex-shrink-0"><div className="animate-shimmer h-96 rounded-xl" /></div>
    </div>
  );
  if (!dest) return <div className="text-center py-16 text-brand-ink/65">Destination not found</div>;

  const redemptions = dest.redemption_options || [];
  const hotels = dest.hotel_options || [];

  return (
    <div className="flex gap-6 pb-16">
      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-6">
        <Link to="/explore" className="text-sm text-brand-ink/65 hover:text-brand-ink/75 transition">&larr; Back to Explore</Link>

        {/* Hero */}
        <div className="animate-fade-in-up bg-navy-light rounded-2xl border border-brand-ink/10 overflow-hidden">
          <div className="relative">
            <DestImage city={dest.city} />
          <div className="relative bg-gradient-to-br from-navy/80 via-teal/20 to-blue-900/60 p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-extrabold">{dest.city}</h1>
                <div className="text-lg text-brand-ink/75">{dest.country} &middot; {dest.airport_codes?.join(', ')}</div>
                <VibeTags tags={dest.vibe_tags} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleCompare(dest.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${compareIds.includes(dest.id) ? 'bg-gold/20 text-gold' : 'bg-brand-ink/10 text-brand-ink/75 hover:bg-brand-ink/20'}`}>
                  {compareIds.includes(dest.id) ? 'In Compare' : 'Compare'}
                </button>
                <button onClick={handleSelect}
                  className="px-4 py-2 bg-teal text-navy font-bold rounded-lg text-sm hover:bg-teal-dark transition">
                  Choose This One
                </button>
              </div>
            </div>
          </div>
          </div>
          <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="September Weather" value={`${dest.weather_high_f}°F`} sub={`${dest.weather_rain_chance} rain`} />
            <Stat label="Daily Budget (2 ppl)" value={`$${dest.avg_daily_budget_min}-${dest.avg_daily_budget_max}`} sub="excl. flights & hotel" />
            <Stat label="Region" value={dest.region} />
            <Stat label="Best Months" value={dest.best_months?.join(', ')} />
          </div>
          <div className="px-4 pb-4"><p className="text-brand-ink/80 text-sm leading-relaxed">{dest.description}</p></div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-navy-light rounded-xl p-1 border border-brand-ink/10 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-2 sm:px-4 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                activeTab === tab.id ? 'bg-teal/20 text-teal' : 'text-brand-ink/70 hover:text-brand-ink/80 hover:bg-brand-ink/5'
              }`}>
              {tab.label}
              {tab.id === 'activities' && activities.length > 0 && <span className="ml-1 text-xs text-brand-ink/55">({activities.length})</span>}
              {tab.id === 'restaurants' && restaurants.length > 0 && <span className="ml-1 text-xs text-brand-ink/55">({restaurants.length})</span>}
            </button>
          ))}
        </div>

        {/* Flights Tab */}
        {activeTab === 'flights' && (
          <div className="space-y-4">
            <div className="text-xs text-brand-ink/65">Pick your flight — click any card for full details. Click "Add to Trip" to lock it in.</div>
            {redemptions.map(r => {
              const selected = isSelected('flight', r.id);
              return (
                <div key={r.id} onClick={() => openModal(r, 'flight')}
                  className={`bg-navy-light rounded-xl border ${selected ? 'border-teal ring-1 ring-teal/30' : r.is_recommended ? 'border-teal/30' : 'border-brand-ink/10'} overflow-hidden cursor-pointer hover:border-white/30 transition`}>
                  {r.is_recommended && !selected && <div className="bg-teal/20 px-4 py-1 text-teal text-xs font-bold uppercase tracking-wider">Recommended</div>}
                  {selected && <div className="bg-teal px-4 py-1 text-navy text-xs font-bold uppercase tracking-wider">Added to Your Trip</div>}
                  <div className="p-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="px-2 py-1 bg-blue-900/50 rounded text-blue-300 font-medium text-xs">{r.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}</span>
                      <span className="text-brand-ink/55">&rarr;</span>
                      <span className="px-2 py-1 bg-brand-ink/10 rounded font-medium text-xs">{r.airline_program}</span>
                      <span className="text-brand-ink/55">&rarr;</span>
                      <span className="text-brand-ink/85 text-xs">{r.routing_description}</span>
                      <span className={`ml-auto px-2 py-0.5 rounded text-xs font-bold uppercase ${r.cabin_class === 'first' ? 'bg-gold/20 text-gold' : 'bg-indigo-500/20 text-indigo-300'}`}>
                        {r.cabin_class}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <MiniStat label="Total (2 ppl)" value={r.total_points_two_travelers?.toLocaleString()} highlight />
                      <MiniStat label="Taxes" value={`$${r.total_estimated_taxes}`} />
                      <MiniStat label="Value" value={<ValueBadge cpp={parseFloat(r.cents_per_point)} />} />
                      <MiniStat label="Duration" value={r.flight_duration_outbound || '—'} />
                      <MiniStat label="Stops" value={
                        r.is_nonstop
                          ? <span className="text-teal font-bold">NONSTOP</span>
                          : <span>{r.num_stops} stop{r.num_stops > 1 ? 's' : ''}</span>
                      } />
                    </div>
                    {r.cash_price_per_person && (
                      <div className="flex items-center gap-2 text-xs text-brand-ink/65">
                        <span>Cash price: <span className="text-brand-ink/80 font-medium">${r.cash_price_per_person?.toLocaleString()}/person</span></span>
                        <span className="text-brand-ink/45">|</span>
                        <span>Points save you <span className="text-teal font-medium">${((r.cash_price_per_person * 2) - r.total_estimated_taxes)?.toLocaleString()}</span></span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <MiniStat label="Transfer" value={<span className={r.transfer_time === 'Instant' ? 'text-teal' : 'text-gold'}>{r.transfer_time}</span>} />
                      <MiniStat label="Aircraft" value={r.aircraft_type || '—'} />
                      <MiniStat label="Seat" value={r.seat_product || '—'} />
                    </div>
                    <PointsBar used={r.total_points_two_travelers} total={r.source_program === 'chase_ur' ? chase : amex} color={r.source_program === 'chase_ur' ? 'bg-indigo-500' : 'bg-blue-500'} />
                    <div className="flex items-center gap-2 pt-1">
                      {selected ? (
                        <button onClick={(e) => { e.stopPropagation(); removeFromTrip('flight', r.id); }}
                          className="px-4 py-2 bg-danger/20 text-danger rounded-lg text-sm font-medium hover:bg-danger/30 transition">
                          Remove from Trip
                        </button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); addToTrip('flight', r.id, r.total_points_two_travelers, r.total_estimated_taxes, r.source_program || 'amex_mr', `${r.airline_program}: ${r.routing_description}`); }}
                          className="px-4 py-2 bg-teal text-navy rounded-lg text-sm font-bold hover:bg-teal-dark transition">
                          Add to Trip
                        </button>
                      )}
                      <span className="text-xs text-brand-ink/55 ml-auto">Click card for full details</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Hotels Tab */}
        {activeTab === 'hotels' && (
          <div className="space-y-4">
            <div className="text-xs text-brand-ink/65">Pick your hotel — click any card for full details. See points vs. cash comparison.</div>
            {hotels.map(h => {
              const selected = isSelected('hotel', h.id);
              return (
                <div key={h.id} onClick={() => openModal(h, 'hotel')}
                  className={`bg-navy-light rounded-xl border ${selected ? 'border-indigo-500 ring-1 ring-indigo-500/30' : h.is_recommended ? 'border-indigo-500/30' : 'border-brand-ink/10'} p-4 space-y-3 cursor-pointer hover:border-white/30 transition`}>
                  {selected && <div className="text-indigo-300 text-xs font-bold uppercase">Added to Your Trip</div>}
                  {!selected && h.is_recommended && <div className="text-indigo-300 text-xs font-bold uppercase">Recommended</div>}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-lg">{h.hotel_name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {h.star_rating && <Stars count={parseFloat(h.star_rating)} />}
                        {h.review_count > 0 && <span className="text-xs text-brand-ink/65">({h.review_count?.toLocaleString()} reviews)</span>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-bold text-lg">{h.points_per_night?.toLocaleString()} <span className="text-xs font-normal text-brand-ink/70">pts/night</span></div>
                      {h.cash_price_per_night > 0 && <div className="text-xs text-brand-ink/65">or ${h.cash_price_per_night}/night cash</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-brand-ink/70">
                    <span>via {h.hotel_program}</span>
                    <span className="text-brand-ink/45">&middot;</span>
                    <span>{h.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}</span>
                    {h.neighborhood && <><span className="text-brand-ink/45">&middot;</span><span>{h.neighborhood}</span></>}
                  </div>
                  <div className="text-sm text-brand-ink/80">{h.description}</div>
                  {h.amenities && h.amenities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {h.amenities.slice(0, 6).map((a, j) => (
                        <span key={j} className="px-2 py-0.5 bg-brand-ink/5 rounded text-[10px] text-brand-ink/70 capitalize">{a}</span>
                      ))}
                      {h.amenities.length > 6 && <span className="text-[10px] text-brand-ink/55">+{h.amenities.length - 6} more</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-sm">
                    <span>{h.nights} nights</span>
                    <span className="text-brand-ink/55">&middot;</span>
                    <span className="font-bold text-lg">{h.total_points?.toLocaleString()} pts total</span>
                    {h.cash_price_per_night > 0 && (
                      <>
                        <span className="text-brand-ink/55">&middot;</span>
                        <span className="text-xs text-brand-ink/65">${(h.cash_price_per_night * h.nights)?.toLocaleString()} cash</span>
                      </>
                    )}
                  </div>
                  {h.source_program === 'chase_ur' && <PointsBar used={h.total_points} total={chase} label="Chase UR" color="bg-indigo-500" />}
                  {h.source_program === 'amex_mr' && <PointsBar used={h.total_points} total={amex} label="Amex MR" color="bg-blue-500" />}
                  <div className="flex items-center gap-2">
                    {selected ? (
                      <button onClick={(e) => { e.stopPropagation(); removeFromTrip('hotel', h.id); }}
                        className="px-4 py-2 bg-danger/20 text-danger rounded-lg text-sm font-medium hover:bg-danger/30 transition">
                        Remove from Trip
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); addToTrip('hotel', h.id, h.total_points, 0, h.source_program, h.hotel_name); }}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-bold hover:bg-indigo-600 transition">
                        Add to Trip
                      </button>
                    )}
                    <span className="text-xs text-brand-ink/55 ml-auto">Click card for full details</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Activities Tab */}
        {activeTab === 'activities' && (
          <div className="space-y-4">
            <div className="text-xs text-brand-ink/65">Add activities to your trip. All prices in cash for 2 people.</div>
            {activities.map(a => {
              const selected = isSelected('activity', a.id);
              return (
                <div key={a.id} className={`bg-navy-light rounded-xl border ${selected ? 'border-teal ring-1 ring-teal/30' : 'border-brand-ink/10'} p-4 space-y-2`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{a.name}</span>
                        {a.rating && <span className="text-xs text-gold">{a.rating}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-brand-ink/65 capitalize">{a.category.replace('_', ' ')}</span>
                        {a.duration && <><span className="text-brand-ink/45">&middot;</span><span className="text-xs text-brand-ink/65">{a.duration}</span></>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gold">${a.price_for_two}</div>
                      <div className="text-[10px] text-brand-ink/55">for 2 people</div>
                    </div>
                  </div>
                  <p className="text-sm text-brand-ink/75">{a.description}</p>
                  <VibeTags tags={a.vibe_tags} size="xs" />
                  {a.booking_tip && (
                    <div className="text-xs text-teal/70 bg-teal/5 rounded-lg p-2">
                      <strong>Tip:</strong> {a.booking_tip}
                    </div>
                  )}
                  {selected ? (
                    <button onClick={() => removeFromTrip('activity', a.id)}
                      className="px-4 py-2 bg-danger/20 text-danger rounded-lg text-sm font-medium hover:bg-danger/30 transition">
                      Remove from Trip
                    </button>
                  ) : (
                    <button onClick={() => addToTrip('activity', a.id, 0, a.price_for_two, null, a.name)}
                      className="px-4 py-2 bg-teal/20 text-teal rounded-lg text-sm font-semibold hover:bg-teal/30 transition">
                      + Add to Trip
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Restaurants Tab */}
        {activeTab === 'restaurants' && (
          <div className="space-y-4">
            <div className="text-xs text-brand-ink/65">Plan your meals. Prices are estimated per meal for 2 people.</div>
            {restaurants.map(r => {
              const selected = isSelected('restaurant', r.id);
              return (
                <div key={r.id} className={`bg-navy-light rounded-xl border ${selected ? 'border-gold ring-1 ring-gold/30' : 'border-brand-ink/10'} p-4 space-y-2`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{r.name}</span>
                        {r.rating && <span className="text-xs text-gold">{r.rating}</span>}
                        <span className="text-xs text-brand-ink/55">{r.price_range}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-brand-ink/65">{r.cuisine}</span>
                        {r.neighborhood && <><span className="text-brand-ink/45">&middot;</span><span className="text-xs text-brand-ink/65">{r.neighborhood}</span></>}
                        <span className="text-brand-ink/45">&middot;</span>
                        <span className="text-xs text-brand-ink/65 capitalize">{r.category.replace('_', ' ')}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gold">${r.avg_meal_for_two}</div>
                      <div className="text-[10px] text-brand-ink/55">for 2 people</div>
                    </div>
                  </div>
                  <p className="text-sm text-brand-ink/75">{r.description}</p>
                  {r.booking_tip && (
                    <div className="text-xs text-gold/70 bg-gold/5 rounded-lg p-2">
                      <strong>Tip:</strong> {r.booking_tip}
                    </div>
                  )}
                  {selected ? (
                    <button onClick={() => removeFromTrip('restaurant', r.id)}
                      className="px-4 py-2 bg-danger/20 text-danger rounded-lg text-sm font-medium hover:bg-danger/30 transition">
                      Remove from Trip
                    </button>
                  ) : (
                    <button onClick={() => addToTrip('restaurant', r.id, 0, r.avg_meal_for_two, null, r.name)}
                      className="px-4 py-2 bg-gold/20 text-gold rounded-lg text-sm font-semibold hover:bg-gold/30 transition">
                      + Add to Trip
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Budget Sidebar */}
      <div className="hidden lg:block w-72 flex-shrink-0 sticky top-24 self-start">
        <BudgetTracker refreshKey={refreshBudget} />
        <div className="mt-4">
          <button onClick={handleSelect}
            className="w-full px-4 py-3 bg-teal text-navy font-bold rounded-xl text-sm hover:bg-teal-dark transition">
            Lock In {dest.city} &rarr; Trip Plan
          </button>
        </div>
      </div>

      {/* Detail Modal */}
      {modalItem && (
        <DetailModal
          item={modalItem}
          type={modalType}
          onClose={closeModal}
          isSelected={isSelected(modalType, modalItem.id)}
          onAdd={() => {
            if (modalType === 'flight') {
              addToTrip('flight', modalItem.id, modalItem.total_points_two_travelers, modalItem.total_estimated_taxes, modalItem.source_program || 'amex_mr', `${modalItem.airline_program}: ${modalItem.routing_description}`);
            } else {
              addToTrip('hotel', modalItem.id, modalItem.total_points, 0, modalItem.source_program, modalItem.hotel_name);
            }
          }}
          onRemove={() => removeFromTrip(modalType, modalItem.id)}
          amex={amex}
          chase={chase}
        />
      )}
    </div>
  );
}

/* ── Detail Modal ── */
function DetailModal({ item, type, onClose, isSelected, onAdd, onRemove, amex, chase }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-navy-light border border-brand-ink/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button onClick={onClose}
          className="absolute top-3 right-3 text-white hover:text-white text-2xl z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 transition shadow-lg">
          &times;
        </button>

        {type === 'flight' ? <FlightModalContent item={item} isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} amex={amex} chase={chase} /> : null}
        {type === 'hotel' ? <HotelModalContent item={item} isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} amex={amex} chase={chase} /> : null}
      </div>
    </div>
  );
}

/* ── Flight Modal Content ── */
function FlightModalContent({ item: r, isSelected, onAdd, onRemove, amex, chase }) {
  const [showSteps, setShowSteps] = useState(false);
  const balance = r.source_program === 'chase_ur' ? chase : amex;

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase ${r.cabin_class === 'first' ? 'bg-gold/20 text-gold' : 'bg-indigo-500/20 text-indigo-300'}`}>
            {r.cabin_class} class
          </span>
          {r.is_nonstop && <span className="px-2.5 py-1 bg-teal/20 rounded text-xs font-bold text-teal">NONSTOP</span>}
          {r.is_recommended && <span className="px-2.5 py-1 bg-teal/10 rounded text-xs font-bold text-teal/70">RECOMMENDED</span>}
        </div>
        <h2 className="text-xl font-bold">{r.routing_description}</h2>
        <div className="text-sm text-brand-ink/70 mt-1">
          via {r.airline_program} &middot; {r.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}
        </div>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold">{r.total_points_two_travelers?.toLocaleString()}</div>
          <div className="text-[10px] text-brand-ink/65 uppercase mt-0.5">Points (2 travelers)</div>
        </div>
        <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold">${r.total_estimated_taxes}</div>
          <div className="text-[10px] text-brand-ink/65 uppercase mt-0.5">Taxes & Fees</div>
        </div>
        <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold"><ValueBadge cpp={parseFloat(r.cents_per_point)} /></div>
          <div className="text-[10px] text-brand-ink/65 uppercase mt-0.5">cpp value</div>
        </div>
      </div>

      {/* Points vs Cash Comparison */}
      {r.cash_price_per_person && (
        <div className="bg-gradient-to-r from-teal/10 to-indigo-500/10 border border-teal/20 rounded-xl p-4">
          <div className="text-xs font-bold text-teal uppercase mb-3">Points vs. Cash Comparison</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-brand-ink/65 mb-1">Cash Price (2 travelers)</div>
              <div className="text-xl font-bold text-brand-ink/80 line-through">${(r.cash_price_per_person * 2)?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-brand-ink/65 mb-1">With Points</div>
              <div className="text-xl font-bold text-teal">{r.total_points_two_travelers?.toLocaleString()} pts + ${r.total_estimated_taxes}</div>
            </div>
          </div>
          <div className="mt-2 text-sm font-medium text-teal">
            You save ${((r.cash_price_per_person * 2) - r.total_estimated_taxes)?.toLocaleString()} by using points
          </div>
        </div>
      )}

      {/* Flight Details */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs text-brand-ink/65 uppercase font-bold">Flight Details</div>
          <div className="text-sm"><span className="text-brand-ink/65">Duration:</span> <span className="font-medium">{r.flight_duration_outbound}</span></div>
          <div className="text-sm"><span className="text-brand-ink/65">Stops:</span> <span className="font-medium">{r.is_nonstop ? 'Nonstop' : `${r.num_stops} stop${r.num_stops > 1 ? 's' : ''}`}</span></div>
          <div className="text-sm"><span className="text-brand-ink/65">Aircraft:</span> <span className="font-medium">{r.aircraft_type}</span></div>
          <div className="text-sm"><span className="text-brand-ink/65">Transfer:</span> <span className={`font-medium ${r.transfer_time === 'Instant' ? 'text-teal' : 'text-gold'}`}>{r.transfer_time}</span></div>
        </div>
        <div className="space-y-2">
          <div className="text-xs text-brand-ink/65 uppercase font-bold">Seat Product</div>
          <div className="text-sm font-medium">{r.seat_product}</div>
          {r.cabin_layout && <div className="text-sm"><span className="text-brand-ink/65">Layout:</span> <span className="font-medium">{r.cabin_layout}</span></div>}
          {r.seat_description && <div className="text-sm text-brand-ink/75">{r.seat_description}</div>}
        </div>
      </div>

      {/* Cabin Amenities */}
      {r.cabin_amenities && r.cabin_amenities.length > 0 && (
        <div>
          <div className="text-xs text-brand-ink/65 uppercase font-bold mb-2">Cabin Amenities</div>
          <div className="grid grid-cols-2 gap-1.5">
            {r.cabin_amenities.map((a, j) => (
              <div key={j} className="flex items-center gap-2 text-sm text-brand-ink/80">
                <span className="w-1.5 h-1.5 rounded-full bg-teal/60 flex-shrink-0" />
                {a}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Points Bar */}
      <div>
        <PointsBar used={r.total_points_two_travelers} total={balance} color={r.source_program === 'chase_ur' ? 'bg-indigo-500' : 'bg-blue-500'} />
        {r.total_points_two_travelers > balance && (
          <div className="bg-danger/10 border border-danger/30 rounded-lg p-2 mt-2 text-xs text-danger">
            This redemption requires {r.total_points_two_travelers?.toLocaleString()} points but you only have {balance?.toLocaleString()}.
          </div>
        )}
      </div>

      {/* Pros & Cons */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-teal font-bold mb-1.5 uppercase">Pros</div>
          {r.pros?.map((p, j) => <div key={j} className="text-sm text-brand-ink/75 mb-1">+ {p}</div>)}
        </div>
        <div>
          <div className="text-xs text-danger font-bold mb-1.5 uppercase">Cons</div>
          {r.cons?.map((c, j) => <div key={j} className="text-sm text-brand-ink/75 mb-1">- {c}</div>)}
        </div>
      </div>

      {r.transfer_time !== 'Instant' && (
        <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 text-sm text-gold">
          Warning: Transfer takes {r.transfer_time}. Award availability may vanish before points arrive.
        </div>
      )}

      {/* Booking Steps */}
      <div>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-sm text-teal font-medium hover:underline">
          {showSteps ? 'Hide' : 'Show'} Booking Steps
        </button>
        {showSteps && (
          <div className="bg-brand-ink/5 rounded-lg p-4 mt-2 text-sm text-brand-ink/80 whitespace-pre-line">{r.booking_steps}</div>
        )}
      </div>

      {/* Action */}
      <div className="flex gap-3 pt-2">
        {isSelected ? (
          <button onClick={onRemove}
            className="flex-1 px-4 py-3 bg-danger/20 text-danger rounded-xl text-sm font-bold hover:bg-danger/30 transition">
            Remove from Trip
          </button>
        ) : (
          <button onClick={onAdd}
            className="flex-1 px-4 py-3 bg-teal text-navy rounded-xl text-sm font-bold hover:bg-teal-dark transition">
            Add to Trip
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Hotel Modal Content ── */
function HotelModalContent({ item: h, isSelected, onAdd, onRemove, amex, chase }) {
  const balance = h.source_program === 'chase_ur' ? chase : amex;
  const totalCash = h.cash_price_per_night * h.nights;

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          {h.star_rating && <Stars count={parseFloat(h.star_rating)} />}
          {h.review_count > 0 && <span className="text-xs text-brand-ink/65">({h.review_count?.toLocaleString()} reviews)</span>}
          {h.is_recommended && <span className="px-2.5 py-1 bg-indigo-500/20 rounded text-xs font-bold text-indigo-300">RECOMMENDED</span>}
        </div>
        <h2 className="text-xl font-bold">{h.hotel_name}</h2>
        <div className="text-sm text-brand-ink/70 mt-1">
          {h.neighborhood && <>{h.neighborhood} &middot; </>}
          via {h.hotel_program} &middot; {h.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-brand-ink/80 leading-relaxed">{h.description}</p>

      {/* Key Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold">{h.points_per_night?.toLocaleString()}</div>
          <div className="text-[10px] text-brand-ink/65 uppercase mt-0.5">Points/Night</div>
        </div>
        <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold">{h.nights}</div>
          <div className="text-[10px] text-brand-ink/65 uppercase mt-0.5">Nights</div>
        </div>
        <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold">{h.total_points?.toLocaleString()}</div>
          <div className="text-[10px] text-brand-ink/65 uppercase mt-0.5">Total Points</div>
        </div>
      </div>

      {/* Points vs Cash Comparison */}
      {h.cash_price_per_night > 0 && (
        <div className="bg-gradient-to-r from-indigo-500/10 to-teal/10 border border-indigo-500/20 rounded-xl p-4">
          <div className="text-xs font-bold text-indigo-300 uppercase mb-3">Points vs. Cash Comparison</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-brand-ink/65 mb-1">Cash Price ({h.nights} nights)</div>
              <div className="text-xl font-bold text-brand-ink/80 line-through">${totalCash?.toLocaleString()}</div>
              <div className="text-xs text-brand-ink/65">${h.cash_price_per_night}/night</div>
            </div>
            <div>
              <div className="text-xs text-brand-ink/65 mb-1">With Points</div>
              <div className="text-xl font-bold text-indigo-300">{h.total_points?.toLocaleString()} pts</div>
              <div className="text-xs text-brand-ink/65">{h.points_per_night?.toLocaleString()}/night</div>
            </div>
          </div>
          {totalCash > 0 && (
            <div className="mt-2 text-sm font-medium text-indigo-300">
              Value: {((totalCash / h.total_points) * 100).toFixed(1)} cents per point &middot; Save ${totalCash?.toLocaleString()} cash
            </div>
          )}
        </div>
      )}

      {/* Amenities */}
      {h.amenities && h.amenities.length > 0 && (
        <div>
          <div className="text-xs text-brand-ink/65 uppercase font-bold mb-2">Amenities</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {h.amenities.map((a, j) => (
              <div key={j} className="flex items-center gap-2 text-sm text-brand-ink/80">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400/60 flex-shrink-0" />
                <span className="capitalize">{a.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Points Bar */}
      <div>
        <PointsBar used={h.total_points} total={balance} label={h.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'} color={h.source_program === 'chase_ur' ? 'bg-indigo-500' : 'bg-blue-500'} />
        <div className="text-xs text-brand-ink/55 mt-1">
          {(balance - h.total_points)?.toLocaleString()} points remaining after booking
        </div>
      </div>

      {/* Pros & Cons */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-teal font-bold mb-1.5 uppercase">Pros</div>
          {h.pros?.map((p, j) => <div key={j} className="text-sm text-brand-ink/75 mb-1">+ {p}</div>)}
        </div>
        <div>
          <div className="text-xs text-danger font-bold mb-1.5 uppercase">Cons</div>
          {h.cons?.map((c, j) => <div key={j} className="text-sm text-brand-ink/75 mb-1">- {c}</div>)}
        </div>
      </div>

      {/* Action */}
      <div className="flex gap-3 pt-2">
        {isSelected ? (
          <button onClick={onRemove}
            className="flex-1 px-4 py-3 bg-danger/20 text-danger rounded-xl text-sm font-bold hover:bg-danger/30 transition">
            Remove from Trip
          </button>
        ) : (
          <button onClick={onAdd}
            className="flex-1 px-4 py-3 bg-indigo-500 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 transition">
            Add to Trip
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Helper Components ── */
function Stars({ count }) {
  const full = Math.floor(count);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={`text-xs ${i < full ? 'text-gold' : 'text-brand-ink/40'}`}>&#9733;</span>
      ))}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs text-brand-ink/65 mb-0.5">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
      {sub && <div className="text-xs text-brand-ink/55">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] text-brand-ink/65 mb-0.5">{label}</div>
      <div className={`text-sm ${highlight ? 'font-bold text-white' : 'font-medium text-brand-ink/85'}`}>{value}</div>
    </div>
  );
}
